<template>
  <div class="h-full flex flex-col pa-2">
    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <v-card outlined class="rounded-lg" v-for="(stat, index) in currentStats" :key="index">
        <div class="d-flex justify-space-between align-center pa-4">
          <div>
            <div class="text-caption grey--text font-weight-bold text-uppercase mb-1">
              {{ stat.title }}
            </div>
            <div class="text-h5 font-weight-bold primary--text">
              {{ stat.value }}
            </div>
          </div>
          <v-avatar :color="stat.color" size="48" class="rounded-lg lighten-5">
            <v-icon :color="stat.iconColor">{{ stat.icon }}</v-icon>
          </v-avatar>
        </div>
        <v-divider></v-divider>
        <div class="px-4 py-2 text-caption grey--text d-flex align-center bg-gray-50">
          <v-icon x-small :color="stat.trend >= 0 ? 'green' : 'red'" class="mr-1">
            {{ stat.trend >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
          </v-icon>
          <span
            :class="stat.trend >= 0 ? 'green--text' : 'red--text'"
            class="font-weight-medium mr-1"
          >
            {{ Math.abs(stat.trend) }}%
          </span>
          <span>vs last month</span>
        </div>
      </v-card>
    </div>

    <!-- Main Table Card -->
    <v-card outlined class="rounded-lg flex-grow-1">
      <v-toolbar flat color="white" height="64" class="px-2 border-b">
        <v-toolbar-title class="text-subtitle-1 font-weight-bold pl-2">
          {{
            type === 'tax'
              ? 'Tax Deducted at Source (TDS) History'
              : 'Provident Fund (PF) Contribution History'
          }}
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <div class="d-flex gap-2">
          <v-btn small outlined color="grey darken-1" class="text-none">
            <v-icon left small>mdi-filter-variant</v-icon> Filter
          </v-btn>
          <v-btn small color="primary" depressed class="text-none">
            <v-icon left small>mdi-download</v-icon> Download Report
          </v-btn>
        </div>
      </v-toolbar>

      <v-data-table
        :headers="currentHeaders"
        :items="currentItems"
        :items-per-page="10"
        class="elevation-0 px-2"
      >
        <!-- Status Column Customization -->
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="getStatusColor(item.status)"
            :text-color="getStatusTextColor(item.status)"
            class="font-weight-medium rounded-pill px-3"
          >
            <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Amount Columns Styling -->
        <template v-slot:item.amount="{ item }">
          <span class="font-weight-bold">{{ item.amount }}</span>
        </template>
        <template v-slot:item.total="{ item }">
          <span class="font-weight-bold">{{ item.total }}</span>
        </template>
        <template v-slot:item.tds="{ item }">
          <span class="font-weight-bold">{{ item.tds }}</span>
        </template>
      </v-data-table>

      <!-- Pagination -->
      <!-- Pagination removed to use default table pagination -->
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'PayrollHistory',
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['tax', 'pf'].includes(value)
    },
    employeeData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      salaryLogs: [],
      // Tax Data
      taxHeaders: [
        { text: 'Month', value: 'month', class: 'text-subtitle-2 grey--text text--darken-1' },
        // {
        //   text: 'Gross Salary',
        //   value: 'grossSalary',
        //   class: 'text-subtitle-2 grey--text text--darken-1'
        // },
        // {
        //   text: 'Taxable Income',
        //   value: 'taxableIncome',
        //   class: 'text-subtitle-2 grey--text text--darken-1'
        // },
        { text: 'TDS Deducted', value: 'tds', class: 'text-subtitle-2 grey--text text--darken-1' },
        { text: 'Status', value: 'status', class: 'text-subtitle-2 grey--text text--darken-1' }
        // { text: 'Payment Date', value: 'date', class: 'text-subtitle-2 grey--text text--darken-1' }
      ],
      taxItems: [],
      taxStats: [
        {
          title: 'Total Tax Paid (YTD)',
          value: '₹0',
          trend: 0,
          color: 'blue lighten-5',
          iconColor: 'blue',
          icon: 'mdi-cash-multiple'
        },
        {
          title: 'Projected Tax',
          value: '₹0',
          trend: 0,
          color: 'orange lighten-5',
          iconColor: 'orange',
          icon: 'mdi-calculator'
        },
        {
          title: 'Last Deduction',
          value: '₹0',
          trend: 0,
          color: 'green lighten-5',
          iconColor: 'green',
          icon: 'mdi-calendar-check'
        }
      ],

      // PF Data
      pfHeaders: [
        { text: 'Month', value: 'month', class: 'text-subtitle-2 grey--text text--darken-1' },
        {
          text: 'Employee Share',
          value: 'employeeShare',
          class: 'text-subtitle-2 grey--text text--darken-1'
        },
        {
          text: 'Employer Share',
          value: 'employerShare',
          class: 'text-subtitle-2 grey--text text--darken-1'
        },
        // {
        //   text: 'Pension Fund (8.33%)',
        //   value: 'pensionFund',
        //   class: 'text-subtitle-2 grey--text text--darken-1'
        // },
        // {
        //   text: 'Total Contribution',
        //   value: 'total',
        //   class: 'text-subtitle-2 grey--text text--darken-1 font-weight-bold'
        // },
        { text: 'Status', value: 'status', class: 'text-subtitle-2 grey--text text--darken-1' }
        // { text: 'Date', value: 'date', class: 'text-subtitle-2 grey--text text--darken-1' }
      ],
      pfItems: [],
      pfStats: [
        {
          title: 'Total PF Balance',
          value: '₹0',
          trend: 0,
          color: 'purple lighten-5',
          iconColor: 'purple',
          icon: 'mdi-piggy-bank'
        },
        {
          title: 'Employer Contribution (YTD)',
          value: '₹0',
          trend: 0,
          color: 'indigo lighten-5',
          iconColor: 'indigo',
          icon: 'mdi-office-building'
        },
        {
          title: 'Last Contribution',
          value: '₹0',
          trend: 0,
          color: 'teal lighten-5',
          iconColor: 'teal',
          icon: 'mdi-history'
        }
      ]
    }
  },
  computed: {
    currentHeaders() {
      return this.type === 'tax' ? this.taxHeaders : this.pfHeaders
    },
    currentItems() {
      return this.type === 'tax' ? this.taxItems : this.pfItems
    },
    currentStats() {
      return this.type === 'tax' ? this.taxStats : this.pfStats
    }
  },
  watch: {
    employeeData: {
      handler(val) {
        if (val && val._id) {
          this.fetchSalaryLogs()
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async fetchSalaryLogs() {
      if (!this.employeeData || !this.employeeData._id) return
      try {
        const response = await this.$axios.get(
          `/orgSetup/salary-logs?employee=${this.employeeData._id}`
        )
        if (response.data.success) {
          this.salaryLogs = response.data.salaryLogs || []
          this.processLogs()
        }
      } catch (error) {
        console.error('Error fetching salary logs:', error)
      }
    },
    processLogs() {
      // Sort logs by date descending to easily get latest
      const sortedLogs = [...this.salaryLogs].sort((a, b) => {
        const dateA = new Date(a.salaryYear, this.getMonthIndex(a.salaryMonth), 1)
        const dateB = new Date(b.salaryYear, this.getMonthIndex(b.salaryMonth), 1)
        return dateB - dateA
      })

      // Process Tax Items
      this.taxItems = sortedLogs.map((log) => ({
        month: `${log.salaryMonth} ${log.salaryYear}`,
        tds: log.incomeTax?.amount ? `₹${log.incomeTax.amount}` : '-',
        status: log.incomeTax?.status || 'Pending'
      }))

      // Process PF Items
      this.pfItems = sortedLogs.map((log) => ({
        month: `${log.salaryMonth} ${log.salaryYear}`,
        employeeShare: log.providentFund?.employeeContribution
          ? `₹${log.providentFund.employeeContribution}`
          : '-',
        employerShare: log.providentFund?.employerContribution
          ? `₹${log.providentFund.employerContribution}`
          : '-',
        status: log.providentFund?.status || 'Pending'
      }))

      this.calculateStats(sortedLogs)
    },
    getMonthIndex(monthName) {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]
      return months.indexOf(monthName)
    },
    calculateStats(logs) {
      if (!logs.length) return

      // --- Tax Stats ---
      const totalTax = logs.reduce((sum, log) => sum + (log.incomeTax?.amount || 0), 0)
      const lastTax = logs[0]?.incomeTax?.amount || 0
      const prevTax = logs[1]?.incomeTax?.amount || 0
      const taxTrend = prevTax > 0 ? ((lastTax - prevTax) / prevTax) * 100 : 0

      // Projected Tax: Simple projection (Avg * 12) or just Total if we treat it as accumulation
      // Using Avg * 12 for a rough projection
      const avgTax = totalTax / logs.length
      const projectedTax = avgTax * 12

      this.taxStats = [
        {
          title: 'Total Tax Paid (YTD)',
          value: `₹${totalTax.toFixed(0)}`,
          trend: parseFloat(taxTrend.toFixed(1)),
          color: 'blue lighten-5',
          iconColor: 'blue',
          icon: 'mdi-cash-multiple'
        },
        {
          title: 'Projected Tax (Est.)',
          value: `₹${projectedTax.toFixed(0)}`,
          trend: 0,
          color: 'orange lighten-5',
          iconColor: 'orange',
          icon: 'mdi-calculator'
        },
        {
          title: 'Last Deduction',
          value: `₹${lastTax}`,
          trend: parseFloat(taxTrend.toFixed(1)),
          color: 'green lighten-5',
          iconColor: 'green',
          icon: 'mdi-calendar-check'
        }
      ]

      // --- PF Stats ---
      const totalPF = logs.reduce(
        (sum, log) =>
          sum +
          (log.providentFund?.employeeContribution || 0) +
          (log.providentFund?.employerContribution || 0),
        0
      )

      const totalEmployerPF = logs.reduce(
        (sum, log) => sum + (log.providentFund?.employerContribution || 0),
        0
      )

      const lastPF =
        (logs[0]?.providentFund?.employeeContribution || 0) +
        (logs[0]?.providentFund?.employerContribution || 0)
      const prevPF =
        (logs[1]?.providentFund?.employeeContribution || 0) +
        (logs[1]?.providentFund?.employerContribution || 0)
      const pfTrend = prevPF > 0 ? ((lastPF - prevPF) / prevPF) * 100 : 0

      this.pfStats = [
        {
          title: 'Total PF Balance',
          value: `₹${totalPF.toFixed(0)}`,
          trend: parseFloat(pfTrend.toFixed(1)),
          color: 'purple lighten-5',
          iconColor: 'purple',
          icon: 'mdi-piggy-bank'
        },
        {
          title: 'Employer Contribution (YTD)',
          value: `₹${totalEmployerPF.toFixed(0)}`,
          trend: 0,
          color: 'indigo lighten-5',
          iconColor: 'indigo',
          icon: 'mdi-office-building'
        },
        {
          title: 'Last Contribution',
          value: `₹${lastPF}`,
          trend: parseFloat(pfTrend.toFixed(1)),
          color: 'teal lighten-5',
          iconColor: 'teal',
          icon: 'mdi-history'
        }
      ]
    },
    getStatusColor(status) {
      const colors = {
        PAID: 'green lighten-5',
        DEPOSITED: 'green lighten-5',
        FILED: 'blue lighten-5',
        PROCESSING: 'orange lighten-5',
        Pending: 'orange lighten-5',
        Completed: 'green lighten-5'
      }
      return colors[status] || 'grey lighten-5'
    },
    getStatusTextColor(status) {
      const colors = {
        PAID: 'green darken-2',
        DEPOSITED: 'green darken-2',
        FILED: 'blue darken-2',
        PROCESSING: 'orange darken-2',
        Pending: 'orange darken-2',
        Completed: 'green darken-2'
      }
      return colors[status] || 'grey darken-2'
    },
    getStatusIcon(status) {
      const icons = {
        PAID: 'mdi-check-circle',
        DEPOSITED: 'mdi-check-circle',
        FILED: 'mdi-file-check',
        PROCESSING: 'mdi-clock-outline',
        Pending: 'mdi-alert-circle',
        Completed: 'mdi-check-circle'
      }
      return icons[status] || 'mdi-circle-small'
    }
  }
}
</script>

<style scoped>
.v-data-table ::v-deep th {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #64748b !important; /* Slate 500 */
}
.v-data-table ::v-deep td {
  font-size: 0.875rem !important;
  color: #334155 !important; /* Slate 700 */
  padding: 16px !important;
}
</style>
